import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * TINA Input — base input primitive.
 *
 * - 16px font-size on mobile (prevents iOS zoom on focus).
 * - Error state driven by `aria-invalid` (set by FormField from RHF errors).
 * - Always wrap in `<FormField>` for label/error wiring; raw `<Input>` is
 *   acceptable only for search bars and one-off filters.
 */
export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = "text", ...props }, ref) => {
    return (
      <input
        ref={ref}
        type={type}
        className={cn(
          "flex h-10 w-full rounded-md border border-slate-300 bg-surface px-3 py-2 text-base",
          "placeholder:text-slate-400",
          "focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/15",
          "disabled:cursor-not-allowed disabled:bg-surface-alt disabled:opacity-60",
          "aria-[invalid=true]:border-danger aria-[invalid=true]:focus-visible:ring-danger/15",
          // file inputs need their own padding tweaks
          "file:border-0 file:bg-transparent file:text-sm file:font-medium",
          className
        )}
        {...props}
      />
    );
  }
);
Input.displayName = "Input";

export { Input };
